import { db } from '@/firebase';
import { Traveller, PaginationOptions, PaginatedResult } from '@/types';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  WhereFilterOp,
  Unsubscribe,
  DocumentSnapshot,
} from 'firebase/firestore';
import { cleanUndefined } from '@/lib/firestoreUtils';
import { 
  createPaginatedQuery, 
  executePaginatedQuery, 
  listenToPaginatedQuery 
} from '@/lib/paginationUtils';

const getTravellersCollection = (tripId: string) => collection(db, 'trips', tripId, 'travellers');

export type TravellerFilter = [string, WhereFilterOp, unknown];

export const listenToTravellers = (
  tripId: string, 
  callback: (travellers: Traveller[]) => void, 
  filters: TravellerFilter[] = []
): (() => void) => {
  const travellersCollection = getTravellersCollection(tripId);
  
  const q = filters.length > 0
    ? query(travellersCollection, ...filters.map(f => where(...f)))
    : travellersCollection;

  return onSnapshot(q, (snapshot) => {
    const travellers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Traveller));
    callback(travellers);
  });
};

// New paginated methods
export const getTravellersPaginated = async (
  tripId: string, 
  paginationOptions: PaginationOptions = {},
  filters: TravellerFilter[] = []
): Promise<PaginatedResult<Traveller>> => {
  const travellersCollection = getTravellersCollection(tripId);
  
  const baseQuery = filters.length > 0
    ? query(travellersCollection, ...filters.map(f => where(...f)))
    : travellersCollection;

  const paginatedQuery = createPaginatedQuery(baseQuery, paginationOptions, 'name');
  
  return executePaginatedQuery(paginatedQuery, (doc: DocumentSnapshot) => ({
    id: doc.id,
    ...doc.data()
  } as Traveller));
};

export const listenToTravellersPaginated = (
  tripId: string,
  callback: (result: PaginatedResult<Traveller>) => void,
  paginationOptions: PaginationOptions = {},
  filters: TravellerFilter[] = []
): Unsubscribe => {
  const travellersCollection = getTravellersCollection(tripId);
  
  const baseQuery = filters.length > 0
    ? query(travellersCollection, ...filters.map(f => where(...f)))
    : travellersCollection;

  const paginatedQuery = createPaginatedQuery(baseQuery, paginationOptions, 'name');
  
  return listenToPaginatedQuery(paginatedQuery, callback, (doc: DocumentSnapshot) => ({
    id: doc.id,
    ...doc.data()
  } as Traveller));
};

export const addTraveller = async (tripId: string, traveller: Omit<Traveller, 'id'>): Promise<string> => {
  const cleanedTraveller = cleanUndefined(traveller);
  const travellersCollection = getTravellersCollection(tripId);
  const docRef = await addDoc(travellersCollection, cleanedTraveller);
  return docRef.id;
};

export const updateTraveller = async (tripId: string, travellerId: string, data: Partial<Traveller>): Promise<void> => {
  if (!travellerId) throw new Error("Traveller ID is missing");
  const cleanedData = cleanUndefined(data);
  const travellerDoc = doc(db, 'trips', tripId, 'travellers', travellerId);
  await updateDoc(travellerDoc, cleanedData);
};

export const deleteTraveller = async (tripId: string, travellerId: string): Promise<void> => {
  const travellerDoc = doc(db, 'trips', tripId, 'travellers', travellerId);
  await deleteDoc(travellerDoc);
};
